import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import BarRestaurantTableCard from './BarRestaurantTableCard';
import BarRestaurantTableDetailModal from './BarRestaurantTableDetailModal';
import LoadingSpinner from './LoadingSpinner';
import './BarRestaurantTableKanban.css';
import AppIcon from './icons/AppIcon';

export default function BarRestaurantTableKanban({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const [activeTurno, setActiveTurno] = useState(null);

  // Carregar projeto
  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId, activeTurno?.id]);

  useEffect(() => {
    if (projectId) {
      loadTurnoAtivo();
    }
  }, [projectId]);

  // Configurar Real-time para mesas
  useEffect(() => {
    if (!projectId || !activeTurno?.id) return;

    console.log('[Bar Restaurant Table Kanban] Configurando Real-time para mesas');

    const tablesChannel = supabase
      .channel(`bar_restaurant_tables:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bar_restaurant_tables',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          console.log('[Bar Restaurant Table Kanban] 🔴 Real-time - Mesa alterada:', payload);
          loadTables(activeTurno.id);
        }
      )
      .subscribe();

    // Cleanup ao desmontar
    return () => {
      console.log('[Bar Restaurant Table Kanban] Desconectando Real-time');
      supabase.removeChannel(tablesChannel);
    };
  }, [projectId]);

  useEffect(() => {
    const handleTurnoUpdate = () => {
      loadTurnoAtivo();
    };

    window.addEventListener('bar-restaurant-turno-updated', handleTurnoUpdate);
    return () => window.removeEventListener('bar-restaurant-turno-updated', handleTurnoUpdate);
  }, []);

  const loadProject = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .eq('company_id', user.company_id)
        .single();

      if (error) throw error;
      console.log('[Bar Restaurant Table Kanban] Projeto carregado:', data.name);
      setProject(data);
    } catch (error) {
      console.error('[Bar Restaurant Table Kanban] Erro ao carregar projeto:', error);
    }
  };

  const loadTurnoAtivo = async () => {
    try {
      const response = await fetch(`/api/bar-restaurant/turnos/active/${projectId}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao carregar turno');
      }

      setActiveTurno(data.turno);
      loadTables(data.turno?.id || null);
    } catch (error) {
      console.error('[Bar Restaurant Table Kanban] Erro ao carregar turno:', error);
    }
  };

  const loadTables = async (turnoId = activeTurno?.id) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data'));
      if (!user?.company_id) return;

      if (!turnoId) {
        setTables([]);
        return;
      }

      const { data, error } = await supabase
        .from('bar_restaurant_tables')
        .select('*')
        .eq('project_id', projectId)
        .eq('company_id', user.company_id)
        .eq('turno_id', turnoId)
        .in('status', ['aberta', 'fechada'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTables(data || []);
    } catch (error) {
      console.error('[Bar Restaurant Table Kanban] Erro ao carregar mesas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTable = (table) => {
    setSelectedTable(table);
    setShowTableModal(true);
  };

  const handleCloseModal = () => {
    setShowTableModal(false);
    setSelectedTable(null);
    loadTables();
  };

  if (!project) {
    return (
      <div className="kanban-container">
        <LoadingSpinner message="Carregando projeto..." />
      </div>
    );
  }

  // Agrupar mesas por status (baseado nas listas do projeto)
  const mesasAbertas = tables.filter(t => t.status === 'aberta');
  const mesasFechadas = tables.filter(t => t.status === 'fechada');

  return (
    <div className="kanban-container">
      {/* Conteúdo */}
      <div className="table-kanban-content">
        {loading ? (
          <LoadingSpinner message="Carregando mesas..." />
        ) : (
          <div className="table-kanban-columns">
            {/* Coluna: Mesas Abertas */}
            <div className="table-kanban-column">
              <div className="column-header">
                <h2>Mesas Abertas</h2>
                <span className="column-count">{mesasAbertas.length}</span>
              </div>
              <div className="tables-list">
                {mesasAbertas.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="event_note" />
                    <p>Nenhuma mesa aberta</p>
                  </div>
                ) : (
                  mesasAbertas.map(table => (
                    <div
                      key={table.id}
                      onClick={() => handleOpenTable(table)}
                      className="table-item"
                    >
                      <BarRestaurantTableCard table={table} />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Coluna: Mesas Fechadas */}
            <div className="table-kanban-column">
              <div className="column-header">
                <h2>Mesas Fechadas</h2>
                <span className="column-count">{mesasFechadas.length}</span>
              </div>
              <div className="tables-list">
                {mesasFechadas.length === 0 ? (
                  <div className="empty-state">
                    <AppIcon name="event_note" />
                    <p>Nenhuma mesa fechada</p>
                  </div>
                ) : (
                  mesasFechadas.map(table => (
                    <div
                      key={table.id}
                      onClick={() => handleOpenTable(table)}
                      className="table-item"
                    >
                      <BarRestaurantTableCard table={table} />
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Modal de Detalhes da Mesa */}
      {showTableModal && selectedTable && (
        <BarRestaurantTableDetailModal
          table={selectedTable}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
